import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { ClerkProvider } from '@clerk/clerk-react'
import './index.css'
// Sentry init runs as a side effect of this import — must come BEFORE
// any component that could throw at render time.
import { Sentry } from './lib/sentry'
import { AuthProvider } from './context/AuthContext'
import AuthGate from './components/Auth/AuthGate'

// Clerk's publishable key is safe to ship to the browser — it identifies
// the Clerk app, not the user. The secret key (sk_*) stays server-side.
//
// Defensive trim: strip BOM (U+FEFF) and surrounding whitespace. Windows
// .env files saved UTF-8-with-BOM and piped into `vercel env add` carry
// the BOM through to the build-time value baked into the JS bundle.
// Clerk's publishable-key validator rejects the prefixed value as
// invalid and the whole React tree fails to mount. Same defense lives
// in lib/neon.js for NEON_DATABASE_URL.
const clerkPublishableKey = (import.meta.env.VITE_CLERK_PUBLISHABLE_KEY || '').replace(/^﻿/, '').trim();
if (!clerkPublishableKey) {
  // Surface this loudly during dev so a missing env var doesn't silently
  // render a blank page. In production the build would have baked in the
  // key already; absence here is a misconfigured deploy.
  console.error('VITE_CLERK_PUBLISHABLE_KEY is not set');
}

// Note: we do NOT wrap AuthGate in a top-level <Routes> here. App.jsx
// owns the application's routing tree with absolute paths (/firm, /crm,
// /insurance, etc.). Nesting those inside an outer wildcard <Route
// path="/*"> blanks the whole tree on React Router v6 (absolute child
// paths don't reliably match under a wildcard parent). AuthGate
// special-cases the /sso-callback URL internally instead.

// ErrorBoundary catches React render-time crashes anywhere in the tree
// and ships them to Sentry. The fallback UI is intentionally minimal —
// real production-quality recovery would be per-feature, but a clean
// "something broke" screen beats a blank page.
const FallbackUI = () => (
  <div className="min-h-screen bg-bone flex items-center justify-center p-6">
    <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-8 max-w-md text-center">
      <h1 className="text-[18px] font-bold text-navy mb-2">Something went wrong</h1>
      <p className="text-[13px] text-[#666] mb-4">
        The error was reported automatically. Try refreshing — if it keeps
        happening, ping Adam.
      </p>
      <button
        onClick={() => window.location.reload()}
        className="px-4 py-2 bg-navy text-white text-[13px] rounded-[4px] hover:bg-navy/90"
      >
        Reload
      </button>
    </div>
  </div>
);

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<FallbackUI />}>
      <BrowserRouter>
        <ClerkProvider publishableKey={clerkPublishableKey}>
          <AuthProvider>
            <AuthGate />
          </AuthProvider>
        </ClerkProvider>
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  </StrictMode>,
)
